/**
 * Google sign-in. Native (Capacitor) uses the native Google plugin and returns
 * an ID token; web (Vite/browser) uses Google Identity Services and returns an
 * OAuth access token. Either way the server verifies the token's audience
 * against our web client id. The client id is PUBLIC (ships in the bundle).
 */

import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

export interface GoogleSignInResult {
  token: string;
  tokenType: 'access' | 'id';
}

const GOOGLE_CLIENT_ID =
  '835053814266-d8qa2fbelp8g8hq4rvnjfo8h1ctc4hmo.apps.googleusercontent.com';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface GisTokenResponse { access_token?: string; error?: string }
interface GisTokenClient { requestAccessToken: () => void }
interface GisGlobal {
  accounts: {
    oauth2: {
      initTokenClient: (cfg: {
        client_id: string;
        scope: string;
        callback: (resp: GisTokenResponse) => void;
      }) => GisTokenClient;
    };
  };
}

let gisLoad: Promise<void> | null = null;

function loadGis(): Promise<void> {
  if (gisLoad) return gisLoad;
  gisLoad = new Promise<void>((resolve, reject) => {
    const existing = (window as unknown as { google?: GisGlobal }).google;
    if (existing?.accounts?.oauth2) { resolve(); return; }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return gisLoad;
}

let nativeInitialized = false;

/** Native Google sign-in (Capacitor plugin) → ID token. Returns null on a
 *  clean user cancel; THROWS on a real error (e.g. code 10 DEVELOPER_ERROR) so
 *  the caller can surface it. */
async function googleSignInNative(): Promise<GoogleSignInResult | null> {
  if (!nativeInitialized) {
    // serverClientId/scopes come from capacitor.config plugins.GoogleAuth.
    GoogleAuth.initialize();
    nativeInitialized = true;
  }
  try {
    return await attemptNativeSignIn();
  } catch (e) {
    const code = String((e as { code?: string | number })?.code ?? '');
    // 12501 = SIGN_IN_CANCELLED → user backed out, no error UI.
    if (code === '12501') return null;
    // A stale native session (signed in previously, app-level logout never
    // called GoogleAuth.signOut) makes the next signIn reject with
    // "Something went wrong" right after account selection. Clear the native
    // session and retry ONCE; rethrow real errors (e.g. 10 DEVELOPER_ERROR).
    try {
      await GoogleAuth.signOut();
    } catch { /* no session to clear — fall through to the retry anyway */ }
    try {
      return await attemptNativeSignIn();
    } catch (e2) {
      const code2 = String((e2 as { code?: string | number })?.code ?? '');
      if (code2 === '12501') return null;
      throw e2;
    }
  }
}

async function attemptNativeSignIn(): Promise<GoogleSignInResult | null> {
  const user = await GoogleAuth.signIn();
  const idToken = (user as { authentication?: { idToken?: string } })?.authentication?.idToken;
  if (!idToken) return null;
  return { token: idToken, tokenType: 'id' };
}

/**
 * Clear the Google session on app logout. Native: signs out of the plugin's
 * GoogleSignInClient — without this, the NEXT signIn rejects against the stale
 * session. Web (GIS token flow) holds no client-side session — no-op. Never
 * throws; logout must proceed regardless.
 */
export async function googleSignOut(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await GoogleAuth.signOut();
  } catch { /* not signed in natively — nothing to clear */ }
}

/** Web Google sign-in (GIS popup) → OAuth access token. */
async function googleSignInWeb(): Promise<GoogleSignInResult | null> {
  await loadGis();
  const google = (window as unknown as { google?: GisGlobal }).google;
  if (!google?.accounts?.oauth2) return null;
  return new Promise<GoogleSignInResult | null>((resolve) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'openid email profile',
      callback: (resp) => resolve(resp?.access_token ? { token: resp.access_token, tokenType: 'access' } : null),
    });
    client.requestAccessToken();
  });
}

/** Sign in with Google. Resolves with the token + how to verify it (or null). */
export async function googleSignIn(): Promise<GoogleSignInResult | null> {
  return Capacitor.isNativePlatform() ? googleSignInNative() : googleSignInWeb();
}
